import React from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import { parseBookingDisplayInstant } from '../../utils/bookingInstant';
import {
  canUserConfirmTutorLessonAttendance,
  hasUserConfirmedTutorLesson,
  shouldShowTutorLessonAttendanceUi,
} from '../../utils/tutorBookingStatus';

export interface TutorLessonAttendanceChipProps {
  userConfirmedAt: string | null | undefined;
  /** Booking status — needed when the icon can trigger confirmation. */
  status?: string;
  /**
   * Org feature gate from the booking payload.
   * When false/missing, hide unconfirmed UI and never show the confirm CTA.
   */
  confirmAttendanceEnabled?: boolean | null;
  /** When true, hide entirely if attendance is not confirmed. */
  hideWhenUnconfirmed?: boolean;
  size?: 'small' | 'medium';
  /** When set with a confirmable booking, the icon becomes the confirm control. */
  onConfirm?: () => void;
  confirming?: boolean;
  /** Use chip contrast color when embedded in a filled status chip. */
  tone?: 'default' | 'inherit';
}

function formatConfirmedAt(iso: string, locale: string): string {
  const date = parseBookingDisplayInstant(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Messenger-style double-tick for lesson attendance.
 * Confirmed = colored DoneAll; awaiting = muted DoneAll (clickable when onConfirm + can confirm).
 */
const TutorLessonAttendanceChip: React.FC<TutorLessonAttendanceChipProps> = ({
  userConfirmedAt,
  status,
  confirmAttendanceEnabled,
  hideWhenUnconfirmed = false,
  size = 'small',
  onConfirm,
  confirming = false,
  tone = 'default',
}) => {
  const { t, i18n } = useTranslation();
  const confirmed = hasUserConfirmedTutorLesson(userConfirmedAt);
  const showUi = shouldShowTutorLessonAttendanceUi({
    status: status ?? 'CONFIRMED',
    userConfirmedAt,
    confirmAttendanceEnabled,
  });
  const canConfirm =
    !!onConfirm &&
    canUserConfirmTutorLessonAttendance({
      status: status ?? '',
      userConfirmedAt,
      confirmAttendanceEnabled,
    });

  if (!showUi || (!confirmed && hideWhenUnconfirmed && !canConfirm)) {
    return null;
  }

  const iconFontSize = size === 'medium' ? 24 : 18;
  const inheritTone = tone === 'inherit';

  if (confirmed) {
    const when = userConfirmedAt ? formatConfirmedAt(userConfirmedAt, i18n.language) : '';
    const title = when
      ? t(
          'userTutorBookings.lessonConfirmation.confirmedAt',
          'You confirmed attendance on {{when}}',
          { when },
        )
      : t('userTutorBookings.lessonConfirmation.confirmed', 'Attendance confirmed');

    return (
      <Tooltip title={title}>
        <Box
          component="span"
          role="img"
          aria-label={title}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: inheritTone ? 'inherit' : 'success.main',
            opacity: inheritTone ? 0.95 : 1,
            lineHeight: 0,
          }}
        >
          <DoneAllRoundedIcon sx={{ fontSize: iconFontSize }} />
        </Box>
      </Tooltip>
    );
  }

  const pendingLabel = canConfirm
    ? t('userTutorBookings.lessonConfirmation.confirm', 'Confirm attendance')
    : t(
        'userTutorBookings.lessonConfirmation.pending',
        'Awaiting attendance confirmation',
      );

  if (canConfirm) {
    return (
      <Tooltip title={pendingLabel}>
        <span>
          <IconButton
            size="small"
            disabled={confirming}
            onClick={(event) => {
              event.stopPropagation();
              onConfirm?.();
            }}
            aria-label={pendingLabel}
            sx={{
              p: 0.25,
              color: inheritTone ? 'inherit' : 'text.secondary',
              opacity: inheritTone ? 0.75 : 1,
              '&:hover': {
                opacity: 1,
                bgcolor: inheritTone ? 'rgba(255,255,255,0.18)' : 'action.hover',
              },
            }}
          >
            {confirming ? (
              <CircularProgress size={iconFontSize - 4} color="inherit" />
            ) : (
              <DoneAllRoundedIcon sx={{ fontSize: iconFontSize }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={pendingLabel}>
      <Box
        component="span"
        role="img"
        aria-label={pendingLabel}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          color: inheritTone ? 'inherit' : 'text.disabled',
          opacity: inheritTone ? 0.55 : 1,
          lineHeight: 0,
        }}
      >
        <DoneAllRoundedIcon sx={{ fontSize: iconFontSize }} />
      </Box>
    </Tooltip>
  );
};

export default TutorLessonAttendanceChip;
